import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Button, Card, Spinner } from '@/components/ui';
import { cn } from '@/lib/utils';
import { ledningsrapport, type LedningsrapportRad } from '@/lib/api';
import { StatCard } from '@/components/features/insikter/StatCard';
import type { LedningsrapportUnderlag } from '../../../../netlify/functions/_shared/ledningsrapport/typer';
import {
  amnenIManaden,
  antalSv,
  datumSv,
  foregaendeManad,
  forandring,
  luckorPerAmne,
  manadKort,
  manadsnamn,
  NYA_LUCKOR_NOT,
  talSv,
  tidpunktSv,
} from '../../../../netlify/functions/_shared/ledningsrapport/format';

/**
 * Fliken Ledningsrapport under Insikter.
 *
 * Vänster: månaderna, nyaste först, med status. Höger: den valda rapporten
 * ritad ur den frysta sifferjsonen (`underlag`) och "I korthet".
 *
 * Integritet: här visas bara typade fält – ämnen och antal. Underlaget kan i
 * teorin bära okända fält; inget av dem renderas.
 *
 * Färg bara där den betyder något: förändringar står i neutral text med
 * tecken (+ / −), aldrig rött eller grönt.
 */
export function LedningsrapportFlik({ arAdmin }: { arAdmin: boolean }) {
  const [rapporter, setRapporter] = useState<LedningsrapportRad[] | null>(null);
  const [laddfel, setLaddfel] = useState<string | null>(null);
  const [vald, setVald] = useState<string | null>(null);

  // State sätts bara när svaret kommer, så att effekten nedan inte skriver state synkront.
  const ladda = useCallback(
    () =>
      ledningsrapport<{ rapporter: LedningsrapportRad[] }>({ atgard: 'lista' }).then(
        (svar) => {
          setLaddfel(null);
          setRapporter(svar.rapporter);
          setVald((fore) => (fore && svar.rapporter.some((r) => r.manad === fore) ? fore : (svar.rapporter[0]?.manad ?? null)));
        },
        (e: unknown) => setLaddfel((e as Error).message),
      ),
    [],
  );

  useEffect(() => {
    void ladda();
  }, [ladda]);

  if (laddfel) {
    return (
      <Card variant="glass" className="p-6 animate-fade-in">
        <p className="text-[13px] text-ink-700">Rapporterna kunde inte hämtas. {laddfel}</p>
        <Button
          variant="secondary"
          size="sm"
          className="mt-3"
          onClick={() => {
            setLaddfel(null);
            setRapporter(null);
            void ladda();
          }}
        >
          Försök igen
        </Button>
      </Card>
    );
  }

  if (!rapporter) {
    return (
      <div className="flex justify-center py-16">
        <Spinner size={24} className="text-brand-500" label="Hämtar rapporter" />
      </div>
    );
  }

  if (rapporter.length === 0) {
    return (
      <Card variant="glass" className="p-8 sm:p-10 animate-fade-in">
        <p className="text-metric-label">Ledningsrapport</p>
        <p className="mt-2 text-[15px] font-semibold text-ink-900">
          Första rapporten skapas 1 november och gäller oktober.
        </p>
        <p className="mt-1.5 text-[12.5px] text-ink-500 max-w-[60ch]">
          Rapporten skapas den 1:a varje månad och visar användning, ämnen, obesvarade frågor och vad som
          åtgärdats – bara ämnen och antal, aldrig frågetext.
        </p>
      </Card>
    );
  }

  const rad = rapporter.find((r) => r.manad === vald) ?? rapporter[0];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[220px_minmax(0,1fr)] gap-5 animate-fade-in">
      <Manadslista rapporter={rapporter} vald={rad.manad} onValj={setVald} />
      <Rapport key={rad.manad} rad={rad} arAdmin={arAdmin} onAndrad={ladda} />
    </div>
  );
}

/* ============================== MÅNADER ============================== */

function Manadslista({
  rapporter,
  vald,
  onValj,
}: {
  rapporter: LedningsrapportRad[];
  vald: string;
  onValj: (manad: string) => void;
}) {
  return (
    <nav aria-label="Rapporter per månad">
      <p className="text-metric-label mb-2 px-1">Månader</p>
      <ul className="flex flex-col gap-1">
        {rapporter.map((r) => {
          const aktiv = r.manad === vald;
          return (
            <li key={r.manad}>
              <button
                type="button"
                aria-current={aktiv ? 'true' : undefined}
                onClick={() => onValj(r.manad)}
                className={cn(
                  'w-full text-left rounded-lg border px-3 py-2.5 transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
                  aktiv
                    ? 'bg-surface border-ink-300 shadow-card'
                    : 'border-transparent hover:bg-surface hover:border-ink-200',
                )}
              >
                <span className="block text-[13px] font-semibold text-ink-900">{versal(manadsnamn(r.manad))}</span>
                <span className="mt-0.5 flex items-center gap-1.5 text-[11.5px] text-ink-500">
                  <Statusprick skickad={!!r.skickad_at} />
                  {r.skickad_at ? `Skickad ${datumSv(stockholmsdatum(r.skickad_at))}` : `Skapad ${datumSv(stockholmsdatum(r.skapad_at))}`}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

/** Fylld prick = skickad, ring = skapad men inte skickad. Form, inte färg. */
function Statusprick({ skickad }: { skickad: boolean }) {
  return (
    <span
      aria-hidden
      className={cn('inline-block w-1.5 h-1.5 rounded-full shrink-0', skickad ? 'bg-ink-700' : 'ring-1 ring-inset ring-ink-500')}
    />
  );
}

/* ============================== RAPPORTEN ============================== */

type Handelse = { typ: 'ok' | 'fel'; text: string } | null;

function Rapport({
  rad,
  arAdmin,
  onAndrad,
}: {
  rad: LedningsrapportRad;
  arAdmin: boolean;
  onAndrad: () => Promise<void>;
}) {
  const u = rad.underlag;
  const [upptagen, setUpptagen] = useState<'pdf' | 'skapa' | 'test' | 'rita' | 'skicka' | null>(null);
  const [handelse, setHandelse] = useState<Handelse>(null);
  const [bekraftaOm, setBekraftaOm] = useState(false);
  const [bekraftaSkicka, setBekraftaSkicka] = useState(false);

  const laddaNer = async () => {
    setUpptagen('pdf');
    setHandelse(null);
    try {
      const { url } = await ledningsrapport<{ url?: string }>({ atgard: 'ladda_ner', manad: rad.manad });
      if (!url) throw new Error('Nedladdningslänken saknas – försök igen.');
      window.location.assign(url);
    } catch (e) {
      const status = (e as { status?: number }).status;
      setHandelse({
        typ: 'fel',
        text: status === 404 ? 'PDF:en är inte klar ännu – försök igen om en stund.' : (e as Error).message,
      });
    } finally {
      setUpptagen(null);
    }
  };

  const skapaOm = async () => {
    setBekraftaOm(false);
    setUpptagen('skapa');
    setHandelse(null);
    let skapad = false;
    try {
      await ledningsrapport({ atgard: 'skapa', manad: rad.manad, skapa_om: true });
      skapad = true;
      // skapa_om nollar pdf_path – rita om PDF:en direkt, annars ger nedladdningen 404.
      const pdf = await ledningsrapport<{ pdf_path?: string | null }>({ atgard: 'pdf', manad: rad.manad });
      setHandelse(
        pdf.pdf_path
          ? { typ: 'ok', text: 'Rapporten är skapad om och PDF:en ritad på nytt.' }
          : { typ: 'fel', text: 'Rapporten är skapad om, men PDF:en hann inte ritas. Försök med Rita PDF.' },
      );
    } catch (e) {
      setHandelse({
        typ: 'fel',
        text: skapad
          ? `Rapporten är skapad om, men PDF:en kunde inte ritas: ${(e as Error).message} Försök med Rita PDF.`
          : (e as Error).message,
      });
    } finally {
      // Även när PDF-steget föll: raden har nya siffror och ingen PDF – listan
      // måste läsas om, annars erbjuds nedladdning och utskick av den gamla.
      if (skapad) await onAndrad().catch(() => undefined);
      setUpptagen(null);
    }
  };

  // Adminens återhämtning när cron inte hann: rita PDF:en och skicka till mottagarna.
  const ritaPdf = async () => {
    setUpptagen('rita');
    setHandelse(null);
    try {
      const svar = await ledningsrapport<{ pdf_path?: string | null; vantar?: boolean }>({ atgard: 'pdf', manad: rad.manad });
      setHandelse(
        svar.vantar
          ? { typ: 'fel', text: 'Rapporten finns inte ännu, så ingen PDF ritades.' }
          : svar.pdf_path
            ? { typ: 'ok', text: 'PDF:en är ritad.' }
            : { typ: 'fel', text: 'Rapporten skapades om medan PDF:en ritades. Försök med Rita PDF igen.' },
      );
      await onAndrad();
    } catch (e) {
      setHandelse({ typ: 'fel', text: (e as Error).message });
    } finally {
      setUpptagen(null);
    }
  };

  const skickaTillMottagarna = async () => {
    setBekraftaSkicka(false);
    setUpptagen('skicka');
    setHandelse(null);
    try {
      const svar = await ledningsrapport<{
        skickade?: boolean;
        vantar?: boolean;
        andrad?: boolean;
        redan_skickad?: boolean;
        antal_mottagare?: number;
      }>({ atgard: 'skicka', manad: rad.manad });
      setHandelse(
        svar.andrad
          ? { typ: 'fel', text: 'Rapporten skapades om eller ritades på nytt under tiden, så inget mejl skickades. Försök igen.' }
          : svar.vantar
          ? { typ: 'fel', text: 'PDF:en är inte klar ännu, så inget mejl skickades.' }
          : svar.redan_skickad
            ? { typ: 'ok', text: 'Rapporten var redan skickad – inget nytt mejl gick ut.' }
            : {
                typ: 'ok',
                text: `Rapporten är skickad till ${antalSv(svar.antal_mottagare ?? 0, 'mottagare', 'mottagare')}.`,
              },
      );
      await onAndrad();
    } catch (e) {
      setHandelse({ typ: 'fel', text: (e as Error).message });
    } finally {
      setUpptagen(null);
    }
  };

  const testmejl = async () => {
    setUpptagen('test');
    setHandelse(null);
    try {
      const svar = await ledningsrapport<{ skickade?: boolean; vantar?: boolean }>({
        atgard: 'skicka',
        manad: rad.manad,
        till: 'mig',
      });
      setHandelse(
        svar.vantar
          ? { typ: 'fel', text: 'PDF:en är inte klar ännu, så inget mejl skickades.' }
          : { typ: 'ok', text: 'Testmejlet är skickat till din adress.' },
      );
    } catch (e) {
      setHandelse({ typ: 'fel', text: (e as Error).message });
    } finally {
      setUpptagen(null);
    }
  };

  const manad = versal(manadsnamn(rad.manad));

  return (
    <article className="min-w-0 space-y-5" aria-labelledby="ledningsrapport-rubrik">
      {/* Huvud */}
      <Card variant="glass" className="p-5 sm:p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-metric-label">Ledningsrapport</p>
            <h2 id="ledningsrapport-rubrik" className="mt-1 text-[22px] font-semibold tracking-[-0.02em] text-ink-900 leading-tight">
              {manad}
            </h2>
            <p className="mt-1.5 text-[12px] text-ink-500">
              Skapad {tidpunktSv(rad.skapad_at)}
              <span className="mx-1.5 opacity-50">·</span>
              {rad.skickad_at ? `Skickad ${tidpunktSv(rad.skickad_at)}` : 'Inte skickad än'}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              onClick={() => void laddaNer()}
              loading={upptagen === 'pdf'}
              disabled={upptagen !== null || !rad.har_pdf}
              aria-describedby={rad.har_pdf ? undefined : 'ledningsrapport-pdf-saknas'}
              leftIcon={<NedGlyf />}
            >
              Ladda ner PDF
            </Button>
            {arAdmin && !rad.har_pdf && (
              <Button size="sm" variant="secondary" onClick={() => void ritaPdf()} loading={upptagen === 'rita'} disabled={upptagen !== null}>
                Rita PDF
              </Button>
            )}
            {arAdmin && !rad.skickad_at && rad.har_pdf && (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setBekraftaSkicka(true)}
                loading={upptagen === 'skicka'}
                disabled={upptagen !== null || bekraftaSkicka || bekraftaOm}
              >
                Skicka till mottagarna
              </Button>
            )}
            {arAdmin && !rad.skickad_at && (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setBekraftaOm(true)}
                loading={upptagen === 'skapa'}
                disabled={upptagen !== null || bekraftaOm || bekraftaSkicka}
              >
                Skapa om
              </Button>
            )}
            {arAdmin && (
              <Button size="sm" variant="secondary" onClick={() => void testmejl()} loading={upptagen === 'test'} disabled={upptagen !== null}>
                Skicka testmejl till mig
              </Button>
            )}
          </div>
        </div>

        {bekraftaOm && (
          <div className="mt-4 rounded-lg border border-ink-200 bg-surface-sunken px-4 py-3 flex flex-wrap items-center justify-between gap-3">
            <p className="text-[12.5px] text-ink-700 max-w-[52ch]">
              Siffrorna för {manadsnamn(rad.manad)} räknas om ur databasen och ersätter de frysta. Sammanfattningen och PDF:en
              skrivs på nytt.
            </p>
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" onClick={() => setBekraftaOm(false)}>
                Avbryt
              </Button>
              <Button size="sm" onClick={() => void skapaOm()}>
                Skapa om rapporten
              </Button>
            </div>
          </div>
        )}

        {bekraftaSkicka && (
          <div className="mt-4 rounded-lg border border-ink-200 bg-surface-sunken px-4 py-3 flex flex-wrap items-center justify-between gap-3">
            <p className="text-[12.5px] text-ink-700 max-w-[52ch]">
              Rapporten för {manadsnamn(rad.manad)} mejlas med PDF:en som bilaga till de mottagare som är inställda för
              ledningsrapporten. Det går inte att ta tillbaka.
            </p>
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" onClick={() => setBekraftaSkicka(false)}>
                Avbryt
              </Button>
              <Button size="sm" onClick={() => void skickaTillMottagarna()}>
                Skicka rapporten
              </Button>
            </div>
          </div>
        )}

        {!rad.har_pdf && (
          <p id="ledningsrapport-pdf-saknas" className="mt-3 text-[12px] text-ink-500">
            {arAdmin
              ? 'PDF:en är inte ritad ännu – rita den med Rita PDF, så går den att ladda ner.'
              : 'PDF:en är inte ritad ännu. Den går att ladda ner när den är klar.'}
          </p>
        )}

        <p
          role="status"
          aria-live="polite"
          className={cn('text-[12.5px] empty:hidden', handelse && 'mt-3', handelse?.typ === 'fel' ? 'text-red-700' : 'text-ink-700')}
        >
          {handelse?.text ?? ''}
        </p>

        <div className="mt-5 pt-5 border-t border-ink-200">
          <p className="text-metric-label">I korthet</p>
          {rad.i_korthet ? (
            <p className="mt-2 text-[14px] leading-relaxed text-ink-800 max-w-[72ch]">{rad.i_korthet}</p>
          ) : (
            <p className="mt-2 text-[13px] text-ink-500 max-w-[72ch]">
              Sammanfattningen kunde inte skrivas när rapporten skapades. Siffrorna nedan gäller.
            </p>
          )}
        </div>
      </Card>

      <Nyckeltal u={u} />
      <Amnen u={u} />
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
        <Luckor u={u} />
        <Atgardat u={u} />
      </div>
      <ElviraManader u={u} />
    </article>
  );
}

/* ============================== NYCKELTAL ============================== */

function Nyckeltal({ u }: { u: LedningsrapportUnderlag }) {
  const fore = foregaendeManad(u.manad);
  const { elvis, elvis_foregaende, elvis_loggas_fran } = u.anvandning;

  // Ingen jämförelse när ELvis inte loggade föregående månad – bara när loggningen började.
  const elvisSub =
    elvis_foregaende === null
      ? elvis_loggas_fran
        ? `loggas från ${datumSv(elvis_loggas_fran)}`
        : 'loggas inte ännu'
      : `${forandring(elvis ?? 0, elvis_foregaende).text} mot ${fore}`;

  const elvira = elviraManad(u);
  const elviraSub =
    elvira.foregaende === null ? `utkast i ${manadKort(u.manad)}` : `${forandring(elvira.antal, elvira.foregaende).text} mot ${fore}`;

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5">
      <StatCard label="Frågor till ELvis" value={elvis} sub={elvisSub} />
      <StatCard label="Utkast från Elvira" value={elvira.antal} sub={elviraSub} />
      <StatCard
        label="Nya luckor"
        value={u.luckor.nya}
        sub={`${forandring(u.luckor.nya, u.luckor.nya_foregaende).text} mot ${fore}`}
      />
      <StatCard label="Stängda luckor" value={u.atgardat.luckor_losta} sub={`${antalSv(u.luckor.oppen_ko, 'öppen', 'öppna')} i kön`} />
    </div>
  );
}

/* ============================== ÄMNEN ============================== */

function Amnen({ u }: { u: LedningsrapportUnderlag }) {
  const rader = amnenIManaden(u);
  const jamforbar = u.anvandning.elvis_foregaende !== null;
  const max = Math.max(1, ...rader.map((r) => r.antal));
  const kort = manadKort(u.manad);

  return (
    <Card variant="glass" className="p-5 sm:p-6">
      <Sektionsrubrik
        titel="Vad som frågas om"
        under={`Frågor till ELvis i ${kort} per ämne. Totalt = alla frågor från start till månadens slut.`}
      />
      <div
        className={cn(
          'grid items-center gap-x-4 text-col-head pb-2 border-b border-ink-200',
          jamforbar ? 'grid-cols-[minmax(0,1fr)_3rem_4rem_3.5rem]' : 'grid-cols-[minmax(0,1fr)_3rem_3.5rem]',
        )}
        aria-hidden
      >
        <span>Ämne</span>
        <span className="text-right">Antal</span>
        {jamforbar && <span className="text-right">Förändr.</span>}
        <span className="text-right">Totalt</span>
      </div>
      <ul>
        {rader.map((r) => (
          <li
            key={r.amne_id}
            className={cn(
              'grid items-center gap-x-4 py-2.5 border-b border-ink-100 last:border-b-0',
              jamforbar ? 'grid-cols-[minmax(0,1fr)_3rem_4rem_3.5rem]' : 'grid-cols-[minmax(0,1fr)_3rem_3.5rem]',
            )}
          >
            <div className="min-w-0">
              <span className="block text-[12.5px] font-semibold text-ink-800 leading-snug sm:truncate">{r.namn}</span>
              <div className="mt-1.5 h-1 bg-ink-100 rounded-full overflow-hidden" aria-hidden>
                {r.antal > 0 && (
                  <div className="h-full bg-brand-500 rounded-full" style={{ width: `${(r.antal / max) * 100}%` }} />
                )}
              </div>
            </div>
            <span className="text-right text-[13px] font-semibold text-ink-900 tabular-nums">
              <span className="sr-only">{r.namn}: </span>
              {talSv(r.antal)}
              <span className="sr-only"> {r.antal === 1 ? 'fråga' : 'frågor'} i {kort}</span>
            </span>
            {jamforbar && (
              <span className="text-right text-[12px] text-ink-500 tabular-nums">{r.forandring.text}</span>
            )}
            <span className="text-right text-[12px] text-ink-500 tabular-nums">
              <span className="sr-only">totalt </span>
              {talSv(r.totalt)}
            </span>
          </li>
        ))}
      </ul>
      <div className="mt-3 space-y-1 text-[11.5px] text-ink-500">
        {!jamforbar && u.anvandning.elvis_loggas_fran && (
          <p>
            Ingen jämförelse med {foregaendeManad(u.manad)} – ELvis loggar varje fråga från{' '}
            {datumSv(u.anvandning.elvis_loggas_fran)}.
          </p>
        )}
        {(u.omarkta_manad ?? 0) > 0 && (
          <p>
            {versal(kort)}: {talSv(u.omarkta_manad)} av månadens frågor har ännu inget ämne och ingår inte i antalet.
          </p>
        )}
        {u.omarkta_totalt > 0 && (
          <p>
            Totalt: {antalSv(u.omarkta_totalt, 'fråga', 'frågor')} sedan start har ännu inget ämne och ingår inte.
          </p>
        )}
      </div>
    </Card>
  );
}

/* ============================== LUCKOR ============================== */

function Luckor({ u }: { u: LedningsrapportUnderlag }) {
  const fore = foregaendeManad(u.manad);
  const rader = luckorPerAmne(u);
  const { unanswered, partial, error } = u.luckor.per_utfall;

  return (
    <Card variant="glass" className="p-5 sm:p-6">
      <Sektionsrubrik
        titel="Obesvarade frågor"
        under={`${antalSv(u.luckor.nya, 'ny lucka', 'nya luckor')} i ${manadKort(u.manad)}, ${forandring(u.luckor.nya, u.luckor.nya_foregaende).text} mot ${fore}.`}
      />
      <dl className="grid grid-cols-3 gap-3 mb-5">
        <Siffra etikett="Helt obesvarade" varde={unanswered} />
        <Siffra etikett="Delvis besvarade" varde={partial} />
        <Siffra etikett="Tekniska fel" varde={error} />
      </dl>
      {rader.length === 0 ? (
        <p className="text-[12.5px] text-ink-500">Inga nya luckor den här månaden eller förra.</p>
      ) : (
        <>
          <div className="grid grid-cols-[minmax(0,1fr)_2.5rem_3.75rem] gap-x-4 text-col-head pb-2 border-b border-ink-200" aria-hidden>
            <span>Ämne</span>
            <span className="text-right">Nya</span>
            <span className="text-right">Förändr.</span>
          </div>
          <ul>
            {rader.map((r) => (
              <li
                key={r.amne_id}
                className="grid grid-cols-[minmax(0,1fr)_2.5rem_3.75rem] gap-x-4 items-baseline py-2 border-b border-ink-100 last:border-b-0"
              >
                <span className="text-[12.5px] text-ink-800 leading-snug sm:truncate">{r.namn}</span>
                <span className="text-right text-[13px] font-semibold text-ink-900 tabular-nums">
                  <span className="sr-only">nya luckor: </span>
                  {talSv(r.antal)}
                </span>
                <span className="text-right text-[12px] text-ink-500 tabular-nums">{r.forandring.text}</span>
              </li>
            ))}
          </ul>
        </>
      )}
      <div className="mt-3 space-y-1 text-[11.5px] text-ink-500">
        <p>{NYA_LUCKOR_NOT}</p>
        <p>Öppen kö vid månadens slut: {antalSv(u.luckor.oppen_ko, 'lucka', 'luckor')} som väntar på åtgärd.</p>
      </div>
    </Card>
  );
}

/* ============================== ÅTGÄRDAT ============================== */

function Atgardat({ u }: { u: LedningsrapportUnderlag }) {
  const a = u.atgardat;
  return (
    <Card variant="glass" className="p-5 sm:p-6">
      <Sektionsrubrik
        titel="Åtgärdat sedan förra rapporten"
        under={`Det som blivit bättre i kunskapsbasen under ${manadKort(u.manad)}.`}
      />
      <dl className="grid grid-cols-2 gap-x-4 gap-y-5">
        <Siffra etikett="Stängda luckor – frågan har nu svar i kunskapsbasen" varde={a.luckor_losta} />
        <Siffra etikett="Rättelser av svar som lösts" varde={a.rattelser_losta} />
        <Siffra etikett="Nya källor i kunskapsbasen" varde={a.nya_kallor} />
        <Siffra etikett="Källor uppdaterade efter ändrade regler på webben" varde={a.regelandringar} />
      </dl>
      <p className="mt-5 pt-4 border-t border-ink-200 text-[12px] text-ink-500">
        Kunskapsbasen vid månadens slut: {antalSv(a.kallor_totalt, 'källa', 'källor')} och{' '}
        {antalSv(a.avsnitt_totalt, 'avsnitt', 'avsnitt')}.
      </p>
    </Card>
  );
}

/* ============================== ELVIRA ============================== */

function ElviraManader({ u }: { u: LedningsrapportUnderlag }) {
  const serie = u.anvandning.elvira;
  if (serie.length === 0) return null;
  const max = Math.max(1, ...serie.map((p) => p.antal));
  const forst = serie[0];
  const sist = serie[serie.length - 1];

  return (
    <Card variant="glass" className="p-5 sm:p-6">
      <Sektionsrubrik
        titel="Utkast från Elvira per månad"
        under={`${versal(manadsnamn(forst.manad).split(' ')[0])}–${manadsnamn(sist.manad)}`}
      />
      <figure>
        <div
          role="img"
          aria-label={serie.map((p) => `${manadKort(p.manad)} ${antalSv(p.antal, 'utkast', 'utkast')}`).join(', ')}
          className="h-28 flex items-end gap-2 sm:gap-4 border-b border-ink-200"
        >
          {serie.map((p) => {
            const aktuell = p.manad.slice(0, 10) === u.manad.slice(0, 10);
            return (
              <div key={p.manad} className="flex-1 h-full flex flex-col justify-end items-center gap-1 min-w-0">
                <span className={cn('text-[11px] tabular-nums', aktuell ? 'text-ink-900 font-semibold' : 'text-ink-500')}>
                  {talSv(p.antal)}
                </span>
                <div
                  className={cn('stapel-vaxer w-full max-w-[44px] rounded-t-[3px] bg-brand-500', !aktuell && 'opacity-40')}
                  style={{ height: `${Math.max(p.antal > 0 ? 4 : 0, (p.antal / max) * 78)}%` }}
                />
              </div>
            );
          })}
        </div>
        <figcaption className="mt-1.5 flex gap-2 sm:gap-4 text-[10.5px] text-ink-500" aria-hidden>
          {serie.map((p) => (
            <span key={p.manad} className={cn('flex-1 text-center truncate', p.manad.slice(0, 10) === u.manad.slice(0, 10) && 'text-ink-800 font-semibold')}>
              {manadKort(p.manad).slice(0, 3)}
            </span>
          ))}
        </figcaption>
      </figure>
    </Card>
  );
}

/* ============================== SMÅDELAR ============================== */

function Sektionsrubrik({ titel, under }: { titel: string; under: ReactNode }) {
  return (
    <div className="mb-4">
      <h3 className="font-bold text-ink-900 text-[15px] leading-tight">{titel}</h3>
      <p className="text-[11.5px] font-semibold text-ink-500 mt-1">{under}</p>
    </div>
  );
}

function Siffra({ etikett, varde }: { etikett: string; varde: number }) {
  return (
    <div className="min-w-0">
      <dt className="text-[11px] text-ink-500 leading-snug">{etikett}</dt>
      <dd className="mt-1 text-[22px] font-semibold tracking-[-0.02em] text-ink-900 leading-none tabular-nums">{talSv(varde)}</dd>
    </div>
  );
}

/** Pil ned mot en linje – egen glyf, inte Lucide. */
function NedGlyf() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M7 2v7M4 6.5 7 9.5l3-3M2.5 12h9" />
    </svg>
  );
}

/* ============================== HJÄLP ============================== */

function versal(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Tidsstämpel → datum i Stockholmstid, "YYYY-MM-DD". */
function stockholmsdatum(iso: string): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Stockholm', year: 'numeric', month: '2-digit', day: '2-digit' }).format(
    new Date(iso),
  );
}

/** Utkast från Elvira i rapportmånaden och månaden före (null om serien inte når dit). */
function elviraManad(u: LedningsrapportUnderlag): { antal: number; foregaende: number | null } {
  const i = u.anvandning.elvira.findIndex((p) => p.manad.slice(0, 10) === u.manad.slice(0, 10));
  if (i === -1) return { antal: 0, foregaende: null };
  return { antal: u.anvandning.elvira[i].antal, foregaende: i > 0 ? u.anvandning.elvira[i - 1].antal : null };
}
